import { REST_schema } from "../../@types/sharedSchema";

/**
 * A failed sales-map request (`sendSalesMapRequest` / `salesMapGetSnapshot`), carrying the
 * server's own `status` and `code`, plus — for a 409 DUPLICATE on contact creation — the
 * existing GHL contact so the caller can offer "link it" instead of just failing.
 *
 * Mirrors `WebotServerError` (see `utils/contacts/contactWriteErrors.ts`): a bare Error with
 * the status interpolated into the message is readable in a console and useless in code.
 */
export class SalesMapError extends Error {
  /** HTTP status. `0` when the request never got a response (offline, DNS, CORS). */
  readonly status: number;
  /** The server's `code` field from `SalesMapErrorResponse`, when the body was JSON. */
  readonly code: REST_schema.SalesMapErrorResponse["code"] | null;
  /** Present only on a 409 DUPLICATE — the GHL contact that already exists. */
  readonly contact: REST_schema.SalesMapGhlContactSummary | null;

  constructor(
    message: string,
    status: number,
    code: REST_schema.SalesMapErrorResponse["code"] | null | undefined,
    contact: REST_schema.SalesMapGhlContactSummary | null | undefined
  ) {
    super(message);
    this.name = "SalesMapError";
    this.status = status;
    this.code = code ?? null;
    this.contact = contact ?? null;
    // tsconfig targets es5, where subclassing a built-in breaks the prototype chain
    // and `instanceof` silently returns false.
    Object.setPrototypeOf(this, SalesMapError.prototype);
  }
}

export function isSalesMapError(error: unknown): error is SalesMapError {
  if (error instanceof SalesMapError) return true;
  return (error as { name?: string } | null)?.name === "SalesMapError";
}
